import React, { useState, useEffect } from 'react';
import PhotoLightbox from '../submission/PhotoLightbox';
import { buildPropertyVideoFilename, downloadImageUrl } from '../../utils/downloadImage';
import {
  getYouTubeThumbnail,
  getYouTubeWatchUrl,
  isYouTubeUrl,
} from '../../utils/youtube';

const ImageGallery = ({ images, video, title = "" }) => {
  const safe = (images || []).filter(Boolean);
  const [current, setCurrent] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [downloadingVideo, setDownloadingVideo] = useState(false);
  const youtubeVideo = video && isYouTubeUrl(video);
  const youtubeWatchUrl = youtubeVideo ? getYouTubeWatchUrl(video) : '';
  const youtubeThumb = youtubeVideo ? getYouTubeThumbnail(video) : '';

  if (!safe.length) return null;

  useEffect(() => {
    if (showVideo || lightboxOpen || safe.length < 2) return undefined;
    const interval = setInterval(() => setCurrent((c) => (c + 1) % safe.length), 4000);
    return () => clearInterval(interval);
  }, [safe.length, showVideo, lightboxOpen]);

  const go = (dir, e) => {
    e?.stopPropagation();
    setCurrent((c) => (c + dir + safe.length) % safe.length);
    setShowVideo(false);
  };

  const openLightbox = (index = current) => {
    if (showVideo) return;
    setCurrent(index);
    setLightboxOpen(true);
  };

  const handleVideoDownload = async (e) => {
    e.stopPropagation();
    if (!video || downloadingVideo || youtubeVideo) return;
    setDownloadingVideo(true);
    try {
      await downloadImageUrl(video, buildPropertyVideoFilename(title, video));
    } finally {
      setDownloadingVideo(false);
    }
  };

  const openYouTube = (e) => {
    e?.stopPropagation();
    if (youtubeWatchUrl) {
      window.open(youtubeWatchUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      <div>
        <div
          className="gallery"
          role={showVideo ? undefined : "button"}
          tabIndex={showVideo ? undefined : 0}
          onClick={() => !showVideo && openLightbox(current)}
          onKeyDown={(e) => {
            if (!showVideo && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              openLightbox(current);
            }
          }}
          aria-label={showVideo ? undefined : "View photos full screen"}
          style={{ cursor: showVideo ? "default" : "pointer" }}
        >
          {showVideo && video ? (
            youtubeVideo ? (
              <button
                type="button"
                className="gallery-youtube-card"
                onClick={openYouTube}
                aria-label="Watch property video on YouTube"
              >
                <img src={youtubeThumb} alt="" className="gallery-youtube-card__thumb" />
                <span className="gallery-youtube-card__overlay" aria-hidden="true">
                  <i className="fab fa-youtube"></i>
                  <span>Watch on YouTube</span>
                </span>
              </button>
            ) : (
              <video src={video} controls autoPlay style={{ width: "100%", height: "100%", objectFit: "contain", background: "#000" }} onClick={(e) => e.stopPropagation()} />
            )
          ) : (
            <img src={safe[current]} className="gallery-main" alt={title || "Property"} />
          )}
          {safe.length > 1 && !showVideo && (
            <>
              <button type="button" className="gallery-nav prev" onClick={(e) => go(-1, e)} aria-label="Previous photo">
                <i className="fas fa-chevron-left"></i>
              </button>
              <button type="button" className="gallery-nav next" onClick={(e) => go(1, e)} aria-label="Next photo">
                <i className="fas fa-chevron-right"></i>
              </button>
            </>
          )}
          <div className="gallery-counter">{showVideo ? (youtubeVideo ? "▶ YouTube" : "▶ Video") : `${current + 1} / ${safe.length}`}</div>
          {!showVideo && (
            <button
              type="button"
              className="agent-gallery-expand"
              onClick={(e) => { e.stopPropagation(); openLightbox(current); }}
              title="Full screen"
              aria-label="Full screen"
            >
              <i className="fas fa-expand"></i>
            </button>
          )}
          {safe.length > 1 && (
            <div className="gallery-dots">
              {safe.map((_, i) => (
                <div
                  key={i}
                  className={`gallery-dot-main ${i === current && !showVideo ? "active" : ""}`}
                  onClick={(e) => { e.stopPropagation(); setCurrent(i); setShowVideo(false); }}
                />
              ))}
              {video && (
                <div
                  className={`gallery-dot-main ${showVideo ? "active" : ""}`}
                  style={{ background: showVideo ? "var(--accent)" : "" }}
                  onClick={(e) => { e.stopPropagation(); setShowVideo(true); }}
                >
                  ▶
                </div>
              )}
            </div>
          )}
          {video && !showVideo && (
            <div className="video-badge" onClick={(e) => { e.stopPropagation(); setShowVideo(true); }}>
              <i className={`${youtubeVideo ? 'fab fa-youtube' : 'fas fa-play'}`}></i>
            </div>
          )}
          {showVideo && (
            <>
              {!youtubeVideo ? (
                <button
                  type="button"
                  className="gallery-video-download"
                  onClick={handleVideoDownload}
                  disabled={downloadingVideo}
                  aria-label="Download video"
                  title="Download video"
                >
                  <i className={`fas ${downloadingVideo ? 'fa-spinner fa-spin' : 'fa-download'}`} aria-hidden="true"></i>
                  Download
                </button>
              ) : (
                <button
                  type="button"
                  className="gallery-video-download"
                  onClick={openYouTube}
                  aria-label="Open video on YouTube"
                  title="Open on YouTube"
                >
                  <i className="fab fa-youtube" aria-hidden="true"></i>
                  YouTube
                </button>
              )}
              <button type="button" className="gallery-nav" style={{ right: "1rem", top: "1rem", transform: "none" }} onClick={(e) => { e.stopPropagation(); setShowVideo(false); }}>
                <i className="fas fa-times"></i>
              </button>
            </>
          )}
        </div>
        <div className="gallery-thumbs">
          {safe.map((img, i) => (
            <img
              key={i}
              src={img}
              className={`gallery-thumb ${i === current && !showVideo ? "active" : ""}`}
              onClick={(e) => { e.stopPropagation(); setCurrent(i); setShowVideo(false); }}
              onDoubleClick={() => openLightbox(i)}
            />
          ))}
          {video && (
            <div
              className={`gallery-thumb ${showVideo ? "active" : ""}`}
              onClick={(e) => { e.stopPropagation(); setShowVideo(true); }}
              style={{ display: "flex", alignItems: "center", justifyContent: "center", background: youtubeVideo ? "#c4302b" : "var(--text)", overflow: "hidden" }}
            >
              {youtubeThumb ? (
                <img src={youtubeThumb} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.92 }} />
              ) : (
                <i className="fas fa-play" style={{ color: "white", fontSize: "0.8rem" }}></i>
              )}
            </div>
          )}
        </div>
      </div>
      {lightboxOpen && (
        <PhotoLightbox photos={safe} startIndex={current} title={title} onClose={() => setLightboxOpen(false)} />
      )}
    </>
  );
};

export default ImageGallery;
